export const DEFAULT_ENTRY_TYPE_OPTIONS = [
  {
    code: "A",
    label: "Part 1 to 5",
    label_gu: "PART-1 TO 5",
    requires: ["guna_number"],
  },
  {
    code: "B",
    label: "Part 2",
    label_gu: "PART-2",
    requires: ["guna_number"],
  },
  {
    code: "C",
    label: "Part 3 Prohibition",
    label_gu: "PART-3 PROHIBITION",
    requires: ["guna_number"],
  },
  {
    code: "JJ",
    label: "Janva Jog (For Information)",
    label_gu: "Janva Jog",
    requires: ["receipt_number"],
  },
  {
    code: "AD",
    label: "Accidental Death",
    label_gu: "Accidental Death",
    requires: ["receipt_number"],
  },
  {
    code: "DITEN",
    label: "M.V. Act 207 Detained",
    label_gu: "M.V. Act 207 Detained",
    requires: ["receipt_number"],
  },
  {
    code: "OTHER",
    label: "Other",
    label_gu: "Other",
    requires: ["receipt_number", "other_entry_type"],
  },
];

export const DEFAULT_ENTRY_TYPE_CODE = "A";

const normalizeString = (value) => String(value ?? "").trim().toLowerCase();

export const normalizeEntryTypeOptions = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return DEFAULT_ENTRY_TYPE_OPTIONS;
  }

  return options.map((option) => ({
    code: String(option?.code ?? "").trim() || DEFAULT_ENTRY_TYPE_CODE,
    label: String(option?.label ?? option?.code ?? "").trim(),
    label_gu: String(option?.label_gu ?? option?.label ?? option?.code ?? "").trim(),
    requires: Array.isArray(option?.requires) ? option.requires : [],
  }));
};

export const findEntryTypeOption = (options, value) => {
  const normalizedOptions = normalizeEntryTypeOptions(options);
  const normalizedValue = normalizeString(value);

  return (
    normalizedOptions.find((option) => {
      return [option.code, option.label, option.label_gu]
        .map(normalizeString)
        .includes(normalizedValue);
    }) || normalizedOptions[0]
  );
};

export const getEntryTypeLabel = (option, selectedLanguage) => {
  if (!option) {
    return "";
  }

  if (selectedLanguage === "gu" && option.label_gu) {
    return option.label_gu;
  }

  return option.label || option.code;
};

export const inferEntryTypeCode = (record, options = DEFAULT_ENTRY_TYPE_OPTIONS) => {
  const matchedOption = findEntryTypeOption(options, record?.entry_type);

  if (matchedOption?.code) {
    return matchedOption.code;
  }

  if (record?.other_entry_type) {
    return "OTHER";
  }

  if (record?.guna_number) {
    return "A";
  }

  if (record?.receipt_number) {
    return "JJ";
  }

  return DEFAULT_ENTRY_TYPE_CODE;
};

export const normalizeEntryTypeRecord = (
  record,
  options = DEFAULT_ENTRY_TYPE_OPTIONS
) => {
  const entryType = findEntryTypeOption(options, inferEntryTypeCode(record, options));

  return {
    ...record,
    entry_type: entryType?.code || DEFAULT_ENTRY_TYPE_CODE,
    guna_number: record?.guna_number || "",
    receipt_number: record?.receipt_number || "",
    other_entry_type: record?.other_entry_type || "",
  };
};

export const getEntryTypeRequirements = (
  entryType,
  options = DEFAULT_ENTRY_TYPE_OPTIONS
) => {
  return findEntryTypeOption(options, entryType)?.requires || ["guna_number"];
};

export const requiresEntryTypeField = (
  entryType,
  fieldName,
  options = DEFAULT_ENTRY_TYPE_OPTIONS
) => {
  return getEntryTypeRequirements(entryType, options).includes(fieldName);
};

export const sanitizeRecordForEntryType = (
  record,
  options = DEFAULT_ENTRY_TYPE_OPTIONS
) => {
  const normalizedRecord = normalizeEntryTypeRecord(record, options);
  const requirements = getEntryTypeRequirements(
    normalizedRecord.entry_type,
    options
  );

  return {
    ...normalizedRecord,
    guna_number: requirements.includes("guna_number")
      ? normalizedRecord.guna_number
      : "",
    receipt_number: requirements.includes("receipt_number")
      ? normalizedRecord.receipt_number
      : "",
    other_entry_type: requirements.includes("other_entry_type")
      ? normalizedRecord.other_entry_type
      : "",
  };
};

export const buildEntryTypeValidationErrors = (
  record,
  options = DEFAULT_ENTRY_TYPE_OPTIONS
) => {
  const normalizedRecord = normalizeEntryTypeRecord(record, options);
  const requirements = getEntryTypeRequirements(
    normalizedRecord.entry_type,
    options
  );

  return {
    entry_type: !normalizedRecord.entry_type,
    guna_number:
      requirements.includes("guna_number") &&
      !String(normalizedRecord.guna_number || "").trim(),
    receipt_number:
      requirements.includes("receipt_number") &&
      !String(normalizedRecord.receipt_number || "").trim(),
    other_entry_type:
      requirements.includes("other_entry_type") &&
      !String(normalizedRecord.other_entry_type || "").trim(),
  };
};

export const inferGunaNumberInputMode = (value) =>
  String(value ?? "").includes("/") ? "formatted" : "simple";

export const formatGunaNumber = (value) => {
  let nextValue = String(value ?? "").replace(/[^a-zA-Z0-9]/g, "");

  if (nextValue.length > 3 && nextValue.length <= 6) {
    nextValue = `${nextValue.slice(0, 3)}/${nextValue.slice(3)}`;
  } else if (nextValue.length > 6) {
    nextValue = `${nextValue.slice(0, 3)}/${nextValue.slice(3, 6)}/${nextValue.slice(6, 8)}`;
  }

  return nextValue;
};

export const sanitizeGunaNumber = (value, mode = "formatted") => {
  if (mode === "simple") {
    return String(value ?? "").replace(/[^0-9]/g, "");
  }

  return formatGunaNumber(value);
};
